/* ===== shared helpers ===== */
function updateCartIconBubble(count) {
  // Update all possible cart bubbles and hide when empty
  const bubbles = document.querySelectorAll('.cart-count-bubble, [data-cart-count-bubble]');
  bubbles.forEach((el) => {
    if (!el) return;
    if (count && count > 0) {
      el.textContent = String(count); // e.g. "3"
      if (el.style) el.style.display = '';
    } else {
      el.textContent = '';
      if (el.style) el.style.display = '';
    }
  });
}

/* Re-index drawer rows so line numbers stay in sync with Shopify */
function reindexCartRows() {
  const rows = document.querySelectorAll('#CartDrawer-CartItems .cart-item');
  rows.forEach((row, idx) => {
    const newIndex = idx + 1;

    // Row id
    row.id = `CartDrawer-Item-${newIndex}`;

    // Quantity input
    const qtyInput = row.querySelector('input[name="updates[]"]');
    if (qtyInput) {
      qtyInput.dataset.index = newIndex;
      qtyInput.id = `Drawer-quantity-${newIndex}`;
    }

    // Error container (if present)
    const errorDiv = row.querySelector('[id^="CartDrawer-LineItemError-"]');
    if (errorDiv) {
      errorDiv.id = `CartDrawer-LineItemError-${newIndex}`;
    }
  });
}

/* ===== remove button element ===== */
class CartRemoveButton extends HTMLElement {
  constructor() {
    super();
    this.addEventListener('click', (event) => {
      event.preventDefault();
      const cartItems = this.closest('cart-items') || this.closest('cart-drawer-items');
      cartItems.updateQuantity(this.dataset.index, 0);
    });
  }
}
customElements.define('cart-remove-button', CartRemoveButton);

/* ===== main cart items controller ===== */
class CartItems extends HTMLElement {
  constructor() {
    super();

    this.lineItemStatusElement =
      document.getElementById('shopping-cart-line-item-status') ||
      document.getElementById('CartDrawer-LineItemStatus');

    this.currentItemCount = Array.from(this.querySelectorAll('[name="updates[]"]')).reduce(
      (total, input) => total + parseInt(input.value || '0', 10),
      0
    );

    /* Save previous qty on focus so Cancel can restore cleanly */
    this.addEventListener('focusin', (e) => {
      const input = e.target;
      if (input && input.matches('input[name="updates[]"]')) {
        input.dataset.prev = input.value;
      }
    });

    this.debouncedOnChange = debounce((event) => this.onChange(event), 150);
    this.addEventListener('change', this.debouncedOnChange.bind(this));
  }

  /* ---------- helpers ---------- */

  moneyNoCents(amountInCents) {
    const dollars = Math.round(amountInCents / 100);
    return `$${dollars}`;        // no "USD"
  }

  formatLineTotal(el, cents) {
    if (!el) return;
    el.textContent = this.moneyNoCents(cents);
  }

  showEmptyState() {
    const wrap = document.getElementById('CartDrawer-CartItems');
    if (!wrap) return;
    wrap.innerHTML = `
      <div class="drawer__inner-empty" style="text-align:center;padding:24px 12px;">
        <h2 class="cart__empty-text" style="margin-bottom:12px;">Your cart is empty</h2>
        <a class="cta-btn cta-btn--outline" href="/collections/all" style="display:inline-flex;width:auto;">Continue Shopping</a>
      </div>
    `;
    const checkoutBtn = document.getElementById('CartDrawer-Checkout');
    if (checkoutBtn) checkoutBtn.setAttribute('disabled', 'disabled');
  }

  buildRemoveConfirm(row, index, input) {
    if (row.classList.contains('cart-item--confirming')) return;

    row.classList.add('cart-item--confirming');

    const confirmBar = document.createElement('div');
    confirmBar.className = 'cart-remove-confirm';
    confirmBar.innerHTML = `
      <div class="cart-remove-confirm__text">Remove this item from your cart?</div>
      <div class="cart-remove-confirm__actions">
        <button type="button" class="cart-remove-confirm__btn cart-remove-confirm__btn--ghost" data-act="cancel">Cancel</button>
        <button type="button" class="cart-remove-confirm__btn cart-remove-confirm__btn--solid" data-act="ok">Remove</button>
      </div>
    `;

    const detailsCell = row.querySelector('.cart-item__details') || row.children[1];
    detailsCell.appendChild(confirmBar);

    const tearDown = () => {
      row.classList.remove('cart-item--confirming');
      confirmBar.remove();
    };

    confirmBar.querySelector('[data-act="cancel"]').addEventListener('click', () => {
      const restore = Math.max(1, parseInt(input.dataset.prev || '1', 10));
      input.value = restore;
      tearDown();
      this.ajaxChange(index, restore).then((cart) => this.refreshNumbers(cart));
    });

    confirmBar.querySelector('[data-act="ok"]').addEventListener('click', () => {
      tearDown();
      this.ajaxChange(index, 0).then((cart) => {
        row.remove();
        this.refreshNumbers(cart);
        if (cart.item_count === 0) this.showEmptyState();
      });
    });
  }

  refreshNumbers(cart) {
    // Re-index DOM rows first so ids/data-index match new line numbers
    reindexCartRows();

    // subtotal
    document.querySelectorAll('.totals__subtotal-value').forEach((el) => {
      el.textContent = this.moneyNoCents(cart.total_price);
    });

    // each line total
    cart.items.forEach((item, i) => {
      const line = document.getElementById(`CartDrawer-Item-${i + 1}`);
      if (!line) return;
      const totalEl = line.querySelector('.right');
      this.formatLineTotal(totalEl, item.final_line_price);
    });

    // cart icon bubble
    updateCartIconBubble(cart.item_count);

    // enable/disable checkout
    const checkoutBtn = document.getElementById('CartDrawer-Checkout');
    if (checkoutBtn) {
      if (cart.item_count > 0) checkoutBtn.removeAttribute('disabled');
      else checkoutBtn.setAttribute('disabled', 'disabled');
    }
  }

  ajaxChange(index, quantity) {
    return fetch(window.Shopify.routes.root + 'cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ line: index, quantity })
    }).then((r) => r.json());
  }

  /* ---------- events ---------- */

  onChange(event) {
    const input = event.target;
    if (!input || !input.matches('input[name="updates[]"]')) return;

    const index = parseInt(input.dataset.index, 10);
    const value = parseInt(input.value || '0', 10);
    const row = document.getElementById(`CartDrawer-Item-${index}`) || input.closest('tr');

    if (value === 0) {
      this.buildRemoveConfirm(row, index, input);
      return;
    }

    this.enableLoading(index);
    this.ajaxChange(index, value)
      .then((cart) => {
        this.refreshNumbers(cart);
        this.disableLoading();
      })
      .catch(() => {
        const errors = document.getElementById('cart-errors') || document.getElementById('CartDrawer-CartErrors');
        if (errors) errors.textContent = (window.cartStrings && window.cartStrings.error) || 'Cart update error';
        this.disableLoading();
      });
  }

  /* ---------- section refresh fallbacks (main cart page) ---------- */

  getSectionsToRender() {
    return [
      {
        id: 'main-cart-items',
        section: document.getElementById('main-cart-items')?.dataset.id,
        selector: '.js-contents'
      },
      { id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '.shopify-section' },
      { id: 'cart-live-region-text', section: 'cart-live-region-text', selector: '.shopify-section' },
      {
        id: 'main-cart-footer',
        section: document.getElementById('main-cart-footer')?.dataset.id,
        selector: '.js-contents'
      }
    ];
  }

  updateQuantity(line, quantity, name) {
    // Fallback path for the full cart page (not drawer)
    this.enableLoading(line);
    const body = JSON.stringify({
      line,
      quantity,
      sections: this.getSectionsToRender().map((s) => s.section),
      sections_url: window.location.pathname
    });

    fetch(`${routes.cart_change_url}`, { ...fetchConfig(), ...{ body } })
      .then((r) => r.text())
      .then((state) => {
        const parsed = JSON.parse(state);
        this.classList.toggle('is-empty', parsed.item_count === 0);

        this.getSectionsToRender().forEach((section) => {
          const elementToReplace =
            document.getElementById(section.id)?.querySelector(section.selector) ||
            document.getElementById(section.id);
          if (elementToReplace && parsed.sections[section.section]) {
            elementToReplace.innerHTML = this.getSectionInnerHTML(parsed.sections[section.section], section.selector);
          }
        });

        updateCartIconBubble(parsed.item_count);

        this.disableLoading();
      })
      .catch(() => {
        this.querySelectorAll('.loading-overlay').forEach((o) => o.classList.add('hidden'));
        const errors = document.getElementById('cart-errors') || document.getElementById('CartDrawer-CartErrors');
        if (errors) errors.textContent = (window.cartStrings && window.cartStrings.error) || 'Cart update error';
        this.disableLoading();
      });
  }

  getSectionInnerHTML(html, selector) {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const node = doc.querySelector(selector);
    return node ? node.innerHTML : '';
  }

  enableLoading(line) {
    const mainCartItems =
      document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    if (mainCartItems) mainCartItems.classList.add('cart__items--disabled');

    const overlays = [
      ...this.querySelectorAll(`#CartItem-${line} .loading-overlay`),
      ...this.querySelectorAll(`#CartDrawer-Item-${line} .loading-overlay`)
    ];
    overlays.forEach((overlay) => overlay.classList.remove('hidden'));

    document.activeElement && document.activeElement.blur();
    if (this.lineItemStatusElement) this.lineItemStatusElement.setAttribute('aria-hidden', false);
  }

  disableLoading() {
    const mainCartItems =
      document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    if (mainCartItems) mainCartItems.classList.remove('cart__items--disabled');
  }
}
customElements.define('cart-items', CartItems);

/* cart-note (unchanged) */
if (!customElements.get('cart-note')) {
  customElements.define(
    'cart-note',
    class CartNote extends HTMLElement {
      constructor() {
        super();
        this.addEventListener(
          'change',
          debounce((event) => {
            const body = JSON.stringify({ note: event.target.value });
            fetch(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body } });
          }, 300)
        );
      }
    }
  );
}
